import { Alert, Input, Modal, Select, Spin, Tooltip } from 'antd';
import { DeleteOutlined, GiftOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { graphql, parse } from 'src/_shared/services/utils.js';
import RuleBuilder from 'src/_shared/components/rule-builder/RuleBuilder';
import {
	cloneConfig,
	isDirty,
	listAtsEntries,
	readConfigFromAtsIntegration,
	scaffoldConfig,
	validateConfig,
	writeConfigIntoAtsIntegration,
} from 'src/_shared/services/job-sync/jobSyncConfig';
import { makeRuleSet, readValueSpec } from 'src/_shared/services/job-sync/serde';
import SaveBar from '../console/SaveBar.jsx';
import Section from '../console/Section.jsx';
import BonusResultControl from './BonusResultControl';
import DefaultsEditor from './DefaultsEditor';
import FieldMappingsEditor from './FieldMappingsEditor';
import ReconciliationSection from './ReconciliationSection';
import ValueSpecEditor from './ValueSpecEditor';
import './job-sync.css';

// The real claraty normalized-job field keys (source-side), used to seed field AutoCompletes.
const FIELD_SUGGESTIONS = [
	'atsJobId', 'title', 'description', 'department', 'atsRequisitionId', 'city', 'state', 'county',
	'country', 'sector', 'applyUrl', 'openedAt', 'createdAt', 'updatedAt', 'jobNumber', 'recruiter',
	'recruiterEmail', 'remoteFlag', 'subCompany', 'positionLevel', 'employmentType', 'hiringManagers',
];

// How many of a tiered bonus's per-tier amounts the read view spells out before collapsing to "+N".
const AMOUNTS_SHOWN = 2;

// Inline editable name + remove for a named rule set (commits on blur / Enter so keystrokes don't
// remount the card mid-rename). The italic `fx` chip is the design's mark for a derived-field set.
function RuleSetName({ name, onRename, onRemove }) {
	const [draft, setDraft] = useState(name);
	useEffect(() => setDraft(name), [name]);
	const commit = () => {
		const trimmed = draft.trim();
		if (trimmed && trimmed !== name) onRename(trimmed);
		else setDraft(name);
	};
	return (
		<>
			<span className="js-fx" aria-hidden>
				fx
			</span>
			<span className="js-token js-setname">
				<Input
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commit}
					onPressEnter={commit}
				/>
			</span>
			<Tooltip title="Remove rule set">
				<button type="button" className="js-iconbtn js-danger" onClick={onRemove} aria-label="Remove rule set">
					<DeleteOutlined />
				</button>
			</Tooltip>
		</>
	);
}

// The Processing tab of the integration editor. Always embedded: the host renders the page header
// (crumb / H1 / Dry-run|Live toggle) — the toggle drives this component through the mode bridge
// (`onModeState` reports the current mode up; `modeActionsRef.current.requestMode` invokes the
// confirm-gated switch here).
export default function JobSyncConfigComponent({
	currentUser,
	company,
	tieredBonuses,
	onDirtyChange,
	onModeState,
	modeActionsRef,
}) {
	const companyId = company?.id || currentUser?.companyId;

	const [loading, setLoading] = useState(true);
	const [atsIntegration, setAtsIntegration] = useState([]);
	const [entries, setEntries] = useState([]);
	const [atsName, setAtsName] = useState(null);
	const [config, setConfig] = useState(null);
	const [initialJson, setInitialJson] = useState('');
	const [isNew, setIsNew] = useState(false);
	const [saving, setSaving] = useState(false);
	const [notice, setNotice] = useState(null); // { type, message }
	const seedRef = useRef(0); // bump to force-remount stateful sub-editors on entry switch

	// ---- load ----
	useEffect(() => {
		let alive = true;
		(async () => {
			setLoading(true);
			const res = await graphql({ input: { id: companyId }, query: 'getATSSettingsByCompany' }, 'cognito');
			if (!alive) return;
			const ats = parse(res?.atsIntegration) || [];
			const list = listAtsEntries(ats);
			setAtsIntegration(ats);
			setEntries(list);
			const preferred = list.find((e) => e.hasConfig) || list[0];
			if (preferred) selectEntry(ats, preferred.atsName);
			setLoading(false);
		})();
		return () => {
			alive = false;
		};
		// reload only when the company changes; selectEntry is stable by usage (no react-hooks
		// disable directive here — the rule isn't registered in this build's lint and the
		// directive itself errors as an unknown rule)
	}, [companyId]);

	function selectEntry(ats, name) {
		const existing = readConfigFromAtsIntegration(ats, name);
		if (existing) {
			const cfg = cloneConfig(existing);
			setConfig(cfg);
			setInitialJson(JSON.stringify(cfg));
			setIsNew(false);
		} else {
			const entry = (parse(ats) || []).find((e) => e && e.ATSName === name);
			const cfg = scaffoldConfig(entry, companyId);
			setConfig(cfg);
			setInitialJson(JSON.stringify(cfg));
			setIsNew(true);
		}
		setAtsName(name);
		setNotice(null);
		seedRef.current += 1;
	}

	const dirty = config && (isNew || isDirty(config, initialJson));
	const errors = useMemo(() => (config ? validateConfig(config) : []), [config]);
	const ruleSetNames = useMemo(() => (config ? Object.keys(config.ruleSets || {}) : []), [config]);

	// Read-view amount suffix for a bonus result: the named tiered bonus's per-tier amounts, listed and
	// never summed — each amount is what ONE user group earns, so a total would misstate what any single
	// referrer gets. The list is capped so the outcome stays on one line: "$10,000 · $3,000 · +1". Null
	// when the name doesn't resolve. Tiers arrive as JSON strings on the AWSJSON field.
	const bonusAmountLabel = (name) => {
		const bonus = (tieredBonuses || []).find((b) => b && b.name === name);
		if (!bonus || !Array.isArray(bonus.tiers) || bonus.tiers.length === 0) return null;
		const amounts = [];
		for (const tier of bonus.tiers) {
			try {
				const parsed = typeof tier === 'string' ? JSON.parse(tier) : tier;
				const n = Number(parsed && parsed.amount);
				if (Number.isFinite(n)) amounts.push(n);
			} catch {
				// malformed tier — skip it
			}
		}
		if (amounts.length === 0) return null;
		const shown = amounts.slice(0, AMOUNTS_SHOWN).map((n) => `$${n.toLocaleString()}`);
		const rest = amounts.length - shown.length;
		return rest > 0 ? `${shown.join(' · ')} · +${rest}` : shown.join(' · ');
	};

	// surface dirty state to the host editor so it can guard navigation away from unsaved work — report
	// only real edits, not the untouched isNew scaffold, so the crumb guard doesn't false-fire on open.
	const changed = !!config && isDirty(config, initialJson);
	useEffect(() => {
		if (onDirtyChange) onDirtyChange(changed);
	}, [changed, onDirtyChange]);

	// ---- mode bridge to the host header ----
	useEffect(() => {
		if (onModeState) onModeState(config ? config.mode : null);
	}, [config, onModeState]);
	useEffect(() => {
		if (modeActionsRef) modeActionsRef.current = { requestMode: onModeChange };
	});

	// ---- edit helpers (immutable) ----
	const patch = (partial) => setConfig((c) => ({ ...c, ...partial }));
	const patchRuleSet = (name, next) =>
		setConfig((c) => ({ ...c, ruleSets: { ...c.ruleSets, [name]: next } }));

	function removeRuleSet(name) {
		setConfig((c) => {
			const next = { ...c.ruleSets };
			delete next[name];
			return { ...c, ruleSets: next };
		});
	}

	function renameRuleSet(oldName, newName) {
		setConfig((c) => {
			if (c.ruleSets[newName]) return c; // don't clobber an existing name
			// preserve insertion order while renaming the key
			const nextRuleSets = {};
			for (const k of Object.keys(c.ruleSets)) {
				nextRuleSets[k === oldName ? newName : k] = c.ruleSets[k];
			}
			// keep fieldMappings { ruleSet } references in sync
			const nextMappings = {};
			for (const [target, m] of Object.entries(c.fieldMappings || {})) {
				nextMappings[target] =
					m && Object.prototype.hasOwnProperty.call(m, 'ruleSet') && m.ruleSet === oldName
						? { ruleSet: newName }
						: m;
			}
			return { ...c, ruleSets: nextRuleSets, fieldMappings: nextMappings };
		});
	}

	function addRuleSet() {
		setConfig((c) => {
			let base = 'ruleSet';
			let i = 1;
			let name = base;
			while (c.ruleSets[name]) name = `${base}${++i}`;
			return { ...c, ruleSets: { ...c.ruleSets, [name]: { ...makeRuleSet(), default: { literal: '' } } } };
		});
	}

	// ---- mode toggle (confirm the dry-run -> live switch) ----
	function onModeChange(nextMode) {
		if (nextMode === 'live' && config.mode !== 'live') {
			Modal.confirm({
				title: 'Switch this feed to Live?',
				content:
					'Live mode lets this configuration create, update and (if enabled) close real jobs on the next run. Continue?',
				okText: 'Switch to Live',
				okButtonProps: { danger: true },
				onOk: () => patch({ mode: 'live' }),
			});
		} else {
			patch({ mode: nextMode });
		}
	}

	// ---- save ----
	async function doSave() {
		setSaving(true);
		setNotice(null);
		try {
			const fresh = await graphql({ input: { id: companyId }, query: 'getATSSettingsByCompany' }, 'cognito');
			const freshArr = parse(fresh?.atsIntegration) || atsIntegration;
			const nextArr = writeConfigIntoAtsIntegration(freshArr, atsName, config);
			const saved = await graphql(
				{ input: { companyId, atsIntegration: JSON.stringify(nextArr) }, update: 'updateATSIntegration' },
				'cognito'
			);
			if (saved === undefined) throw new Error('The save request failed.');
			setAtsIntegration(nextArr);
			setEntries(listAtsEntries(nextArr));
			setInitialJson(JSON.stringify(config));
			setIsNew(false);
			setNotice({ type: 'success', message: 'Job-sync configuration saved.' });
		} catch (e) {
			setNotice({ type: 'error', message: e.message || 'Could not save the configuration.' });
		}
		setSaving(false);
	}

	function handleSaveClick() {
		if (errors.length) {
			setNotice({ type: 'error', message: 'Fix the highlighted issues before saving.' });
			return;
		}
		const rec = config.reconciliation || {};
		const risky = config.mode === 'live' || rec.closeMissing;
		if (risky) {
			Modal.confirm({
				title: isNew ? 'Create this configuration?' : 'Save changes to this feed?',
				content: (
					<span>
						This config is <b>{config.mode === 'live' ? 'Live' : 'Dry-run'}</b>
						{rec.closeMissing ? (
							rec.closeSource ? (
								<>
									{' '}and will <b>close missing jobs</b> whose source is{' '}
									<code>{rec.closeSource}</code>
								</>
							) : (
								<>
									{' '}and will <b>close every open job absent from the feed</b>, including other-ATS
									and manually-created jobs
								</>
							)
						) : null}
						. Continue?
					</span>
				),
				okText: isNew ? 'Create' : 'Save',
				okButtonProps: { danger: config.mode === 'live' },
				onOk: doSave,
			});
		} else {
			doSave();
		}
	}

	function handleReset() {
		const cfg = JSON.parse(initialJson);
		setConfig(cfg);
		seedRef.current += 1;
		setNotice(null);
	}

	// ---- render ----
	if (loading) {
		return (
			<div className="js-loading">
				<Spin />
			</div>
		);
	}

	if (!config) {
		return (
			<Alert
				type="info"
				showIcon
				message="No ATS integration found"
				description="Create an ATS integration in the ATS settings above first — the job-sync configuration attaches to it."
			/>
		);
	}

	return (
		<>
			{isNew && (
				<Alert
					style={{ marginBottom: 16 }}
					type="info"
					showIcon
					message="New configuration — not saved yet"
					description="This integration has no job-sync config. Fill it in and Create to replace the seed script."
				/>
			)}

			{/* entry picker when more than one ATS entry could host a config */}
			{entries.length > 1 && (
				<div style={{ margin: '-6px 0 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
					<span className="ei-minilabel" style={{ margin: 0 }}>
						Integration
					</span>
					<div className="js-token" style={{ width: 260 }}>
						<Select
							value={atsName}
							onChange={(name) => {
								if (dirty) {
									Modal.confirm({
										title: 'Discard unsaved changes?',
										okText: 'Discard',
										onOk: () => selectEntry(atsIntegration, name),
									});
								} else {
									selectEntry(atsIntegration, name);
								}
							}}
							style={{ width: '100%' }}
							options={entries.map((e) => ({
								value: e.atsName,
								label: `${e.atsName}${e.hasConfig ? '' : ' — new'}`,
							}))}
						/>
					</div>
				</div>
			)}

			<ReconciliationSection key={`recon-${seedRef.current}`} config={config} patch={patch} />

			<Section
				id="sec-rules"
				title="Transform"
				description="One reusable rule builder per derived field. Rules read top-to-bottom; the first match wins."
				bare
			>
				<RuleBuilder
					key={`bonus-${seedRef.current}`}
					title="Bonus mapping"
					icon={<GiftOutlined />}
					value={config.bonus || { rules: [] }}
					onChange={(next) => patch({ bonus: next })}
					outLabel="bonus"
					fieldOptions={FIELD_SUGGESTIONS}
					truncateAt={1}
					note="bonus name → resolved to a tiered bonus by erin-connect"
					renderResult={(spec, onSpec) => (
						<BonusResultControl spec={spec} onChange={onSpec} tieredBonuses={tieredBonuses} />
					)}
					renderResultRead={(spec) => {
						const { value: bonusName } = readValueSpec(spec);
						if (bonusName == null || bonusName === '') return <span className="js-kw">not set</span>;
						const amounts = bonusAmountLabel(String(bonusName));
						// Both spans inherit their colour so the Otherwise row's quiet-ink override still wins —
						// RuleBuilder renders the default row through this same callback.
						// The separator is a middot, not a dash: bonus names routinely carry their own headline
						// figure ("TS_SCI with Poly - $10,000"), so a dash here would collide with the one in
						// the name and read as a range.
						return (
							<>
								<span className="js-res-name">{String(bonusName)}</span>
								{amounts ? <span className="js-res-amt">{`· ${amounts}`}</span> : null}
							</>
						);
					}}
				/>

				{/* Named rule sets */}
				{ruleSetNames.map((name) => (
					<RuleBuilder
						key={`${name}-${seedRef.current}`}
						title={
							<RuleSetName
								name={name}
								onRename={(newName) => renameRuleSet(name, newName)}
								onRemove={() => removeRuleSet(name)}
							/>
						}
						value={config.ruleSets[name]}
						onChange={(next) => patchRuleSet(name, next)}
						outLabel="set"
						fieldOptions={FIELD_SUGGESTIONS}
						renderResult={(spec, onSpec) => (
							<ValueSpecEditor spec={spec} onChange={onSpec} fieldOptions={FIELD_SUGGESTIONS} />
						)}
					/>
				))}
				<div style={{ marginTop: 14 }}>
					<button type="button" className="js-linkbtn" onClick={addRuleSet}>
						<PlusOutlined /> Add rule set
					</button>
				</div>
			</Section>

			<FieldMappingsEditor
				key={`maps-${seedRef.current}`}
				fieldMappings={config.fieldMappings || {}}
				ruleSetNames={ruleSetNames}
				fieldOptions={FIELD_SUGGESTIONS}
				onChange={(next) => patch({ fieldMappings: next })}
			/>

			<DefaultsEditor
				key={`defs-${seedRef.current}`}
				defaults={config.defaults || {}}
				onChange={(next) => patch({ defaults: next })}
			/>

			{notice && (
				<Alert
					style={{ marginTop: 14 }}
					type={notice.type}
					showIcon
					closable
					message={notice.message}
					onClose={() => setNotice(null)}
				/>
			)}

			<SaveBar
				tone={isNew ? 'new' : dirty ? 'dirty' : 'clean'}
				statusText={isNew ? 'Not created yet' : dirty ? 'Unsaved changes' : 'All changes saved'}
				sub="Match, transform, mapping and default changes save here."
				errors={errors}
				onReset={handleReset}
				onSave={handleSaveClick}
				resetDisabled={!dirty || isNew}
				saveDisabled={!dirty || errors.length > 0}
				saving={saving}
				saveLabel={isNew ? 'Create configuration' : 'Save changes'}
				live={config.mode === 'live'}
			/>
		</>
	);
}
